import React, { useState } from 'react';
import PropTypes from 'prop-types';

const LegacyHealingIntake = ({
  // Text content props
  welcomeText = "Welcome to",
  brandName = "Legacy Healing",
  suffixText = "Digital Intake",
  description = [
    "We're here to support your journey toward healing.",
    "This intake form will take about 10–15 minutes to complete.",
    "You can pause and continue later if needed."
  ],
  confidentialityText = "All information is confidential and securely stored in compliance with HIPAA.",
  terms = [
    "I agree to the terms and confirm my information is accurate.",
    "I understand this form may include sensitive health and legal information.",
    "I consent to Legacy Healing contacting me if necessary."
  ],
  ctaText = "Begin Intake",
  
  // Style props
  primaryColor = "#1F3047",
  secondaryColor = "#349AC8",
  textColor = "#1F3047",
  welcomeFontSize = 32,
  brandFontSize = 40,
  suffixFontSize = 32,
  bodyFontSize = 16,
  
  // Logo props
  logo = null,
  logoHeight = 60,
  onNext = () => {}
}) => {
  const [checkedItems, setCheckedItems] = useState(terms.map(() => false));

  const toggleCheckbox = (index) => {
    const newCheckedItems = [...checkedItems];
    newCheckedItems[index] = !newCheckedItems[index];
    setCheckedItems(newCheckedItems);
  };

  const allChecked = checkedItems.every(item => item);

  return (
    <div style={containerStyle}>
      {/* Logo (conditionally rendered) */}
      {logo && (
        <div style={logoContainerStyle}>
          <img 
            src={logo} 
            alt={`${brandName} Logo`} 
            style={{ 
              ...logoStyle, 
              height: `${logoHeight}px`,
              '@media (max-width: 768px)': {
                height: `${logoHeight * 0.8}px`
              }
            }} 
          />
        </div>
      )}

      {/* Welcome Message */}
      <div style={messageContainerStyle}>
        <div style={textRowStyle}>
          <span style={{
            ...textStyle,
            color: primaryColor,
            fontSize: `clamp(24px, 4vw, ${welcomeFontSize}px)`,
            fontFamily: 'Mauren',
            fontWeight: 100,
          }}>
            {welcomeText}
          </span>
          
          <span style={{
            ...textStyle,
            color: secondaryColor,
            fontSize: `clamp(28px, 5vw, ${brandFontSize}px)`,
            fontFamily: "'Turbinado Pro'",
            fontWeight: 400,
          }}>
            {brandName}
          </span>
          
          <span style={{
            ...textStyle,
            color: primaryColor,
            fontSize: `clamp(24px, 4vw, ${suffixFontSize}px)`,
            fontFamily: 'Mauren, sans-serif',
            fontWeight: 100,
          }}>
            {suffixText}
          </span>
        </div>

        {/* Description */}
        <div style={descriptionContainerStyle}>
          <div style={{
            ...paragraphStyle,
            fontSize: `clamp(14px, 3vw, ${bodyFontSize}px)`,
            lineHeight: '1.5'
          }}>
            {description.map((line, index) => (
              <React.Fragment key={index}>
                {line}
                {index < description.length - 1 && <br />}
              </React.Fragment>
            ))}
          </div>
          
          <div style={{
            ...paragraphStyle,
            fontSize: `clamp(14px, 3vw, ${bodyFontSize}px)`,
            marginTop: '8px'
          }}>
            {confidentialityText}
          </div>
        </div>

        {/* Terms and Conditions Checkboxes */}
        <div style={termsContainerStyle}>
          {terms.map((term, index) => (
            <div 
              key={index} 
              style={termItemStyle}
              onClick={() => toggleCheckbox(index)}
            >
              <div style={{
                ...checkboxStyle,
                outline: `1px ${primaryColor} solid`,
                backgroundColor: checkedItems[index] ? primaryColor : 'transparent'
              }}>
                {checkedItems[index] && (
                  <div style={checkmarkStyle} />
                )}
              </div>
              <div style={{
                ...termTextStyle,
                fontSize: `clamp(14px, 3vw, ${bodyFontSize}px)`,
                color: primaryColor
              }}>
                {term}
              </div>
            </div>
          ))}
        </div>

        {/* Call-to-Action Button */}
        <button 
          onClick={onNext}
          style={{
            ...buttonStyle,
            backgroundColor: secondaryColor,
            opacity: allChecked ? 1 : 0.6,
            cursor: allChecked ? 'pointer' : 'not-allowed'
          }}
          disabled={!allChecked}
        >
          {ctaText}
        </button>
      </div>
    </div>
  );
};

// Style objects
const containerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  maxWidth: '800px',
  margin: '0 auto',
  padding: 'clamp(20px, 5vw, 40px) clamp(16px, 4vw, 20px)',
  textAlign: 'center',
  boxSizing: 'border-box',
};

const logoContainerStyle = {
  marginBottom: 'clamp(20px, 4vw, 30px)',
};

const logoStyle = {
  width: 'auto',
  maxWidth: '100%',
};

const messageContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'clamp(16px, 3vw, 24px)',
  width: '100%',
};

const textRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 'clamp(8px, 2vw, 16px)',
};

const textStyle = {
  wordWrap: 'break-word',
  whiteSpace: 'nowrap',
  margin: 0,
  lineHeight: 1.2,
  '@media (max-width: 480px)': {
    whiteSpace: 'normal'
  }
};

const descriptionContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 'clamp(4px, 1vw, 8px)',
  width: '100%',
};

const paragraphStyle = {
  color: '#1F3047',
  fontFamily: "'FONTSPRING DEMO - Commuters Sans'",
  fontWeight: 400,
  wordWrap: 'break-word',
  textAlign: 'center',
  margin: 0,
  marginTop: 'clamp(16px, 3vw, 26px)',
};

const termsContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 'clamp(12px, 2vw, 16px)',
  width: '100%',
  marginLeft: 'clamp(12px, 20vw, 800px)',
  padding: '0 clamp(16px, 4vw, 32px)',
  marginTop: 'clamp(16px, 3vw, 26px)',
  boxSizing: 'border-box',
  '@media (max-width: 768px)': {
    alignItems: 'center'
  }
};

const termItemStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'clamp(8px, 2vw, 16px)',
  cursor: 'pointer',
  width: '100%',
  maxWidth: '600px',
  '@media (max-width: 768px)': {
    width: '90%'
  }
};

const checkboxStyle = {
  width: 'clamp(20px, 4vw, 24px)',
  height: 'clamp(20px, 4vw, 24px)',
  minWidth: 'clamp(20px, 4vw, 24px)',
  borderRadius: '4px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  transition: 'all 0.2s ease',
  marginTop: '2px'
};

const checkmarkStyle = {
  width: 'clamp(12px, 3vw, 15px)',
  height: 'clamp(8px, 2vw, 10px)',
  borderBottom: '2px solid white',
  borderLeft: '2px solid white',
  transform: 'rotate(-45deg)',
  marginBottom: '2px',
};

const termTextStyle = {
  fontFamily: 'FONTSPRING DEMO - Commuters Sans',
  fontWeight: 400,
  lineHeight: '1.5',
  wordWrap: 'break-word',
  textAlign: 'left',
  flex: 1,
};

const buttonStyle = {
  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 3vw, 24px)',
  color: 'white',
  fontSize: 'clamp(14px, 3vw, 16px)',
  fontWeight: '500',
  border: 'none',
  borderRadius: '20px',
  transition: 'all 0.3s ease',
  marginTop: 'clamp(16px, 3vw, 20px)',
  width: 'clamp(140px, 30vw, 200px)',
  maxWidth: '300px',
  ':hover': {
    opacity: 0.9,
    transform: 'translateY(-2px)',
  },
  '@media (max-width: 480px)': {
    width: '80%',
    maxWidth: 'none'
  }
};

// PropTypes
LegacyHealingIntake.propTypes = {
  welcomeText: PropTypes.string,
  brandName: PropTypes.string,
  suffixText: PropTypes.string,
  description: PropTypes.arrayOf(PropTypes.string),
  confidentialityText: PropTypes.string,
  terms: PropTypes.arrayOf(PropTypes.string),
  ctaText: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  textColor: PropTypes.string,
  welcomeFontSize: PropTypes.number,
  brandFontSize: PropTypes.number,
  suffixFontSize: PropTypes.number,
  bodyFontSize: PropTypes.number,
  logo: PropTypes.string,
  logoHeight: PropTypes.number,
  onNext: PropTypes.func
};

export default LegacyHealingIntake;